/**
 * Display helpers for appointment client labels (anonymous guest vs registered userId).
 */

import type { Appointment } from '@/api/mcp-appointments';

/** Contact + label resolved from auth portal customers. */
export interface CustomerDisplayInfo {
  /** Full chip/select label: `name (email)` / email / id. */
  label: string;
  /** Name only when available (details modal title prefers this). */
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** Minimal user fields needed for a display label. */
export interface CustomerDisplaySource {
  id: string;
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

/**
 * Formats a portal customer for UI labels (matches RegisteredCustomerSelector).
 */
export function formatCustomerDisplayName(user: CustomerDisplaySource): string {
  const name = user.name?.trim();
  const email = user.email?.trim();
  if (name) {
    return email ? `${name} (${email})` : name;
  }
  return email || user.id;
}

/**
 * Resolves the client label shown on calendar chips and details.
 * Priority: anonymousName → registered customer map → pending/anonymous fallback.
 * Never returns a raw userId UUID.
 */
export function getAppointmentClientDisplayLabel(
  appointment: Pick<Appointment, 'anonymousName' | 'userId'>,
  pendingLabel: string,
  userDisplayById?: Map<string, CustomerDisplayInfo> | Record<string, CustomerDisplayInfo>
): string {
  const anonymousName = appointment.anonymousName?.trim();
  if (anonymousName) {
    return anonymousName;
  }

  const userId = appointment.userId?.trim();
  if (userId && userDisplayById) {
    const info =
      userDisplayById instanceof Map
        ? userDisplayById.get(userId)
        : userDisplayById[userId];
    if (info?.label?.trim()) {
      return info.label.trim();
    }
  }

  return pendingLabel;
}
